
import { PLACEHOLDER_DATA_URI } from '@/assets/placeholder'
import { fetchPublicConfig } from '@/utils/publicConfig'
import { resolveOrderLocation } from '@/utils/orderLocation'
export default {
  name: 'OrderArchive',
  data () {
    return {
      orders: [],
      loading: false,
      // 公共配置（自提地址当前值）：resolver 的旧数据 fallback 依据
      publicCfg: null,
      filters: {
        startDate: '',
        endDate: '',
        status: '',
        keyword: '',
        fulfillment_mode: ''
      },
      pagination: {
        total: 0,
        page: 1,
        limit: 50,
        totalPages: 1
      },
      selectedIds: [],
      selectAll: false,
      statusMap: {
        pending: '待确认',
        picking: '拣货中',
        packed: '已打包',
        shipped: '已发货',
        shipping: '配送中',
        delivering: '运输中',
        completed: '已完成',
        cancelled: '已取消'
      },
      // 合并相关
      mergeInterval: 24,
      autoMergePending: false,
      expandedGroups: [],
      selectedOrder: null,
      _refreshTimer: null
    }
  },
  created () {
    // 自提地址当前值：旧数据无 snapshot 时 resolver 的 fallback 依据
    fetchPublicConfig().then(cfg => { this.publicCfg = cfg }).catch(() => {})
    this.fetchOrders()
  },
  mounted () {
    this._startPolling()
  },
  beforeDestroy () {
    this._stopPolling()
  },
  computed: {
    // 归档单地址 resolver：pickup 单保留取货地址快照（履约历史，不跟门店搬家）
    selectedLoc () {
      return resolveOrderLocation(this.selectedOrder, this.publicCfg)
    },
    mergedOrders () {
      if (this.mergeInterval === null) {
        // 不合并，每个订单单独显示
        return this.orders.map(order => this.formatSingleOrder(order))
      }
      return this.processMergedOrders()
    }
  },
  methods: {
    // 获取履约模式显示文本
    getFulfillmentModeText (mode) {
      const modeMap = {
        courier: '配送',
        pickup: '自提',
        express: '快递'
      }
      return modeMap[mode] || '配送'
    },

    // === 自动刷新（30s 轮询，页面不可见时跳过） ===
    _startPolling () {
      this._stopPolling()
      this._refreshTimer = setInterval(() => {
        if (!document.hidden) {
          this.fetchOrders()
        }
      }, 30000)
    },
    _stopPolling () {
      if (this._refreshTimer) {
        clearInterval(this._refreshTimer)
        this._refreshTimer = null
      }
    },

    async fetchOrders () {
      this.loading = true
      try {
        const params = new URLSearchParams({
          page: this.pagination.page,
          limit: this.pagination.limit
        })
        if (this.filters.startDate) params.append('startDate', this.filters.startDate)
        if (this.filters.endDate) params.append('endDate', this.filters.endDate)
        if (this.filters.status) params.append('status', this.filters.status)
        if (this.filters.keyword) params.append('keyword', this.filters.keyword)
        if (this.filters.fulfillment_mode) params.append('fulfillment_mode', this.filters.fulfillment_mode)

        const res = await this.axios.get(`/admin/orders/archived?${params}`)
        if (res.data.code === 200) {
          this.orders = res.data.data
          this.pagination.total = res.data.pagination.total
          this.pagination.totalPages = Math.ceil(this.pagination.total / this.pagination.limit)
        }
      } catch (err) {
        console.error('获取归档订单失败:', err)
      } finally {
        this.loading = false
      }
    },
    changePage (page) {
      this.pagination.page = page
      this.fetchOrders()
    },
    toggleSelectAll () {
      if (this.selectAll) {
        this.selectedIds = this.orders.map(o => o.id)
      } else {
        this.selectedIds = []
      }
    },
    formatDate (dateStr) {
      if (!dateStr) return '-'
      const d = new Date(dateStr)
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
    },
    goBack () {
      this.$router.push('/admin/orders')
    },

    // 格式化单个订单为组格式
    formatSingleOrder (order) {
      return {
        id: order.id,
        isMerged: false,
        orderCount: 1,
        customerName: order.contact_name || order.user_nickname,
        customerPhone: order.contact_phone || order.user_phone,
        wechatName: order.wechat_name,
        wechatId: order.wechat_id,
        totalAmount: order.total_amount,
        totalQuantity: order.total_quantity,
        createdAt: order.created_at,
        status: order.status,
        remark: order.remark,
        fulfillment_mode: order.fulfillment_mode || 'courier',
        contactName: order.contact_name,
        contactPhone: order.contact_phone,
        contactAddress: order.contact_address,
        pickup_address_snapshot: order.pickup_address_snapshot != null ? String(order.pickup_address_snapshot) : null,
        productSummary: order.items?.map(item => ({
          name: item.product_name,
          image: item.product_image,
          quantity: item.quantity,
          isGift: item.is_gift
        })) || [],
        children: [order]
      }
    },

    // 处理合并订单
    processMergedOrders () {
      const groups = new Map()

      this.orders.forEach(order => {
        const key = this.getMergeKey(order)
        if (!groups.has(key)) {
          groups.set(key, [])
        }
        groups.get(key).push(order)
      })

      return Array.from(groups.values()).map(orders => {
        if (orders.length === 1) {
          return this.formatSingleOrder(orders[0])
        }

        // 合并多个订单
        const first = orders[0]
        const allItems = orders.flatMap(o => o.items || [])

        // 合并相同商品（非赠品）
        const productMap = new Map()
        allItems.forEach(item => {
          const key = `${item.product_id}_${item.is_gift ? 'gift' : 'normal'}`
          if (productMap.has(key)) {
            const existing = productMap.get(key)
            existing.quantity += item.quantity
          } else {
            productMap.set(key, {
              name: item.product_name,
              image: item.product_image,
              quantity: item.quantity,
              isGift: item.is_gift
            })
          }
        })

        return {
          id: `merge_${first.id}`,
          isMerged: true,
          orderCount: orders.length,
          customerName: first.contact_name || first.user_nickname,
          customerPhone: first.contact_phone || first.user_phone,
          wechatName: first.wechat_name,
          wechatId: first.wechat_id,
          totalAmount: orders.reduce((sum, o) => sum + o.total_amount, 0),
          totalQuantity: orders.reduce((sum, o) => sum + o.total_quantity, 0),
          createdAt: first.created_at,
          status: first.status,
          remark: orders.map(o => o.remark).filter(r => r).join('; '),
          contactName: first.contact_name,
          contactPhone: first.contact_phone,
          contactAddress: first.contact_address,
          fulfillment_mode: first.fulfillment_mode || 'courier',
          pickup_address_snapshot: first.pickup_address_snapshot != null ? String(first.pickup_address_snapshot) : null,
          productSummary: Array.from(productMap.values()),
          children: orders
        }
      }).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    },

    // 获取合并键
    getMergeKey (order) {
      const timeSlot = this.getTimeSlot(order.created_at)
      const phone = order.contact_phone || order.user_phone || 'unknown'
      return `${phone}_${timeSlot}`
    },

    // 获取时间槽
    getTimeSlot (timestamp) {
      const date = new Date(timestamp)
      const hours = date.getHours()
      const dateStr = date.toISOString().split('T')[0]

      if (this.mergeInterval === 24) {
        return dateStr
      } else if (this.mergeInterval === 12) {
        return `${dateStr}_${hours < 12 ? 'AM' : 'PM'}`
      } else {
        const slot = Math.floor(hours / this.mergeInterval)
        return `${dateStr}_${slot}`
      }
    },

    // 展开/收起订单组
    toggleExpand (group) {
      const index = this.expandedGroups.indexOf(group.id)
      if (index > -1) {
        this.expandedGroups.splice(index, 1)
      } else {
        this.expandedGroups.push(group.id)
      }
    },

    // 获取显示的商品（最多4个）
    getDisplayProducts (group) {
      return group.productSummary.slice(0, 4)
    },

    // 显示订单详情
    showOrderDetail (orderOrGroup) {
      this.selectedOrder = orderOrGroup
    },
    async unarchiveOrder (groupOrOrder) {
      if (!confirm('确定要恢复该订单吗？')) return

      try {
        let orderIds
        if (typeof groupOrOrder === 'object' && groupOrOrder.isMerged) {
          // 合并订单组，获取所有子订单ID
          orderIds = groupOrOrder.children.map(o => o.id)
        } else if (typeof groupOrOrder === 'object') {
          // 单个订单对象
          orderIds = [groupOrOrder.id]
        } else {
          // 直接传递的ID字符串
          orderIds = [groupOrOrder]
        }

        const res = await this.axios.post('/admin/orders/unarchive', { orderIds })
        if (res.data.code === 200) {
          this.fetchOrders()
        }
      } catch (err) {
        console.error('恢复失败:', err)
      }
    },
    async batchUnarchive () {
      // 从选中的订单组中提取所有实际订单ID
      const orderIds = []
      this.mergedOrders.forEach(group => {
        if (this.selectedIds.includes(group.id)) {
          if (group.isMerged) {
            // 合并订单组，添加所有子订单ID
            orderIds.push(...group.children.map(o => o.id))
          } else {
            // 单个订单
            orderIds.push(group.id)
          }
        }
      })

      if (orderIds.length === 0) {
        alert('请选择要恢复的订单')
        return
      }

      if (!confirm(`确定要恢复选中的 ${orderIds.length} 个订单吗？`)) return
      try {
        const res = await this.axios.post('/admin/orders/unarchive', { orderIds })
        if (res.data.code === 200) {
          this.selectedIds = []
          this.selectAll = false
          this.fetchOrders()
        }
      } catch (err) {
        console.error('批量恢复失败:', err)
      }
    },
    // 物理删除入口已移除（订单生命周期治理 §15）：正式页面不再提供订单物理删除
    exportCSV () {
      const params = new URLSearchParams()
      if (this.filters.startDate) params.append('startDate', this.filters.startDate)
      if (this.filters.endDate) params.append('endDate', this.filters.endDate)
      if (this.filters.status) params.append('status', this.filters.status)
      if (this.filters.keyword) params.append('keyword', this.filters.keyword)

      const token = localStorage.getItem('token')
      const url = `${this.axios.defaults.baseURL}/admin/orders/export?${params}`

      // 创建下载链接
      const a = document.createElement('a')
      a.href = url
      a.download = `orders_archive_${new Date().toISOString().split('T')[0]}.csv`
      a.target = '_blank'
      // 添加认证头通过设置
      fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(res => res.blob())
        .then(blob => {
          const url = window.URL.createObjectURL(blob)
          a.href = url
          a.click()
          window.URL.revokeObjectURL(url)
        })
    }
  }
}
